// utils/screenDetection.js
// Funzioni pure: no setState, facili da testare con console.log()

/** Estrae n frame distanziati di 'intervalMs' dal <video>. */
export async function collectFrames(videoEl, n = 8, intervalMs = 60) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  canvas.width = videoEl.videoWidth || 640;
  canvas.height = videoEl.videoHeight || 480;

  const frames = [];
  for (let i = 0; i < n; i++) {
    ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
    const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
    frames.push(img);
    // Piccola attesa tra i frame
    // eslint-disable-next-line no-await-in-loop
    await new Promise(r => setTimeout(r, intervalMs));
  }
  return frames;
}

/** Varianza frame-to-frame per bande verticali (sensibile a Moiré/refresh). */
export function moireScore(frames, stripeWidth = 8) {
  if (frames.length < 2) return 0;
  let acc = 0, count = 0;
  for (let k = 1; k < frames.length; k++) {
    const a = frames[k - 1], b = frames[k];
    const w = a.width, h = a.height;
    const da = a.data, db = b.data;
    for (let x = 0; x < w; x += stripeWidth) {
      let stripeDiff = 0, pixels = 0;
      for (let y = 0; y < h; y++) {
        const idx = (y * w + x) * 4;
        // luminanza approx
        const la = 0.2126 * da[idx] + 0.7152 * da[idx + 1] + 0.0722 * da[idx + 2];
        const lb = 0.2126 * db[idx] + 0.7152 * db[idx + 1] + 0.0722 * db[idx + 2];
        stripeDiff += Math.abs(lb - la);
        pixels++;
      }
      acc += stripeDiff / Math.max(1, pixels);
      count++;
    }
  }
  return acc / Math.max(1, count); // più alto → più probabile schermo
}

/** Stima una frequenza di flicker (grezza) analizzando un singolo pixel nel tempo. */
export function estimateRefresh(frames, sampleX = 16, sampleY = 16) {
  if (frames.length < 4) return 0;
  const series = [];
  for (const f of frames) {
    const { width: w, data } = f;
    const idx = (sampleY * w + sampleX) * 4;
    const l = 0.2126 * data[idx] + 0.7152 * data[idx + 1] + 0.0722 * data[idx + 2];
    series.push(l);
  }
  // Conta cambi di direzione (una proxy semplice del “ritmo”)
  let turns = 0;
  for (let i = 2; i < series.length; i++) {
    const d1 = series[i - 1] - series[i - 2];
    const d2 = series[i] - series[i - 1];
    if (d1 === 0 || d2 === 0) continue;
    if ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) turns++;
  }
  return turns; // più alto → possibile refresh regolare
}

/** Rapporto componente blu / luminanza (schermi più “emissivi” nel blu in media). */
export function blueRatio(frame) {
  const { width: w, height: h, data } = frame;
  let sumB = 0, sumL = 0, step = 4 * 4; // campionamento
  for (let i = 0; i < data.length; i += step) {
    const r = data[i], g = data[i + 1], b = data[i + 2];
    const l = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    sumB += b;
    sumL += l;
  }
  return sumL ? (sumB / sumL) : 0;
}

/** Differenza speculare tra frame con e senza flash. */
export function flashReflectionScore(frameNoFlash, frameFlash) {
  const a = frameNoFlash, b = frameFlash;
  if (!a || !b) return 0;
  const { width: w, height: h } = a;
  const da = a.data, db = b.data;

  // Analizza solo la regione centrale (meno bordi e vignettature)
  const x0 = Math.floor(w * 0.2), x1 = Math.floor(w * 0.8);
  const y0 = Math.floor(h * 0.2), y1 = Math.floor(h * 0.8);

  let spec = 0, cnt = 0;
  for (let y = y0; y < y1; y += 2) {
    for (let x = x0; x < x1; x += 2) {
      const idx = (y * w + x) * 4;
      const la = 0.2126 * da[idx] + 0.7152 * da[idx + 1] + 0.0722 * da[idx + 2];
      const lb = 0.2126 * db[idx] + 0.7152 * db[idx + 1] + 0.0722 * db[idx + 2];
      const d = lb - la; // quanto cresce la specularità con flash
      spec += d > 0 ? d : 0;
      cnt++;
    }
  }
  return spec / Math.max(1, cnt); // più alto → superficie speculare (schermo)
}

/** Decisione pesata (tuning iniziale, poi si aggiusta su campioni reali). */
// utils/screenDetection.js (solo la funzione)
export function decideScreenLike({ moire, refreshTurns, blue }) {
  const toNum = (v, fallback = 0) => (Number.isFinite(v) ? v : fallback);

  const mo = toNum(moire, 0);
  const rt = toNum(refreshTurns, 0);
  const bl = toNum(blue, 0.9); // safe baseline near neutral

  // Thresholds exposed for diagnostic logging — values unchanged
  const thresholds = {
    moireMax:           8,     // moire normalised over this
    refreshMax:         6,     // refreshTurns normalised over this
    blueMin:            0.85,  // blue ratio lower bound
    blueRange:          0.30,  // blue ratio range (0.85 → 1.15 maps to 0 → 1)
    weightMoire:        0.6,
    weightRefresh:      0.2,
    weightBlue:         0.2,
    isScreenThreshold:  0.6,
  };

  const moireNorm = Math.min(Math.max(mo / thresholds.moireMax, 0), 1);
  const refhNorm  = Math.min(Math.max(rt / thresholds.refreshMax, 0), 1);
  const blueNorm  = Math.min(Math.max((bl - thresholds.blueMin) / thresholds.blueRange, 0), 1);

  const score = thresholds.weightMoire   * moireNorm
              + thresholds.weightRefresh * refhNorm
              + thresholds.weightBlue    * blueNorm;
  const safeScore = Number.isFinite(score) ? score : 0;
  const isScreen  = safeScore >= thresholds.isScreenThreshold;

  // Contribution of each component toward the final score (diagnostic only)
  const components = {
    moireNorm:      +moireNorm.toFixed(4),
    refhNorm:       +refhNorm.toFixed(4),
    blueNorm:       +blueNorm.toFixed(4),
    moireContrib:   +(thresholds.weightMoire   * moireNorm).toFixed(4),
    refreshContrib: +(thresholds.weightRefresh * refhNorm).toFixed(4),
    blueContrib:    +(thresholds.weightBlue    * blueNorm).toFixed(4),
  };

  // Human-readable reason string — lists which signals exceeded their thresholds
  const parts = [];
  if (moireNorm > 0)  parts.push(`moire=${mo.toFixed(2)}/${thresholds.moireMax}(contrib:${components.moireContrib})`);
  if (refhNorm  > 0)  parts.push(`refreshTurns=${rt}/${thresholds.refreshMax}(contrib:${components.refreshContrib})`);
  if (blueNorm  > 0)  parts.push(`blue=${bl.toFixed(3)}>=${thresholds.blueMin}(contrib:${components.blueContrib})`);
  const triggerReason = isScreen
    ? (parts.length ? parts.join(' + ') : 'unknown') + ` => score=${safeScore.toFixed(3)}>=${thresholds.isScreenThreshold}`
    : `score=${safeScore.toFixed(3)}<${thresholds.isScreenThreshold} (not triggered)`;

  return { score: safeScore, isScreen, triggerReason, thresholds, components };
}

